// Custom loader cho next/image: ảnh nằm trên statics thì đi qua imgproxy, KHÔNG
// còn dùng image optimizer trong pod Next nữa. Ảnh local và host lạ vẫn đi
// /_next/image như cũ (xem nextOptimizer bên dưới).
//
// URL sinh ra là LOCAL và ĐỌC ĐƯỢC, giữ nguyên key ảnh:
//   /imgproxy/w:640/q:75/image/b16db17c56b24670a624b31f4ef30dc0.webp
// rồi route src/pages/api/imgproxy dịch sang cú pháp imgproxy và gọi service trong
// cluster. Nhìn URL/Network tab/log là biết ngay ảnh nào.
//
// VÌ SAO KHÔNG NHÉT THẲNG CÚ PHÁP IMGPROXY VÀO ĐÂY:
//   dạng plain của imgproxy là /plain/s3://bucket/key@webp, mà '//' trong 's3://'
//   làm path-to-regexp KHÔNG match nổi rewrite '/imgproxy/:path*' - thử qua Next
//   thật thì trả 308 redirect và request không bao giờ tới imgproxy. Dạng base64 thì
//   match được nhưng mất id trong URL. Nên mình tự định nghĩa path đọc được, còn việc
//   dựng URL imgproxy (kể cả '@webp' bắt buộc, vì imgproxy đang bật
//   SKIP_PROCESSING_FORMATS=jpeg,jpg) để route làm ở server.
//
// Đuôi '.webp' giữ ở cuối URL để CDN cache theo phần mở rộng như mọi file ảnh khác.
//
// Bỏ trống NEXT_PUBLIC_IMGPROXY_PREFIX là quay về image optimizer mặc định của Next
// (next.config.mjs không gắn loaderFile nữa) - đó là đường rollback, nhưng cần build lại.

const PREFIX = process.env.NEXT_PUBLIC_IMGPROXY_PREFIX;

// Các host media của mình -> bucket s3 tương ứng. imgproxy chỉ mở đúng 2 bucket này
// trong IMGPROXY_ALLOWED_SOURCES. statics.flyer.us là CloudFront của cùng object
// (đối chiếu etag 17/17 id thật đều trùng với statics.flyer.vn) nên map về bucket .vn.
// PHẢI KHỚP với bảng cùng tên trong src/pages/api/imgproxy/[...path].ts.
const BUCKET_BY_HOST = {
  'statics.flyer.vn': 'statics.flyer.vn',
  'statics.flyer.us': 'statics.flyer.vn',
  'statics1.flyer.vn': 'statics1.flyer.vn',
  'statics1.flyer.us': 'statics1.flyer.vn',
};

// URL đọc được chỉ chở KEY, không chở bucket - bucket do route suy ra từ cùng biến
// NEXT_PUBLIC_BASE_MEDIA_URL này. Nên ở đây chỉ nhận ảnh thuộc đúng bucket của môi
// trường đang chạy; ảnh của bucket khác (vd render URL statics1 khi đang ở prod) rơi
// về /_next/image cho an toàn thay vì trỏ sai bucket.
const ENV_BUCKET = (() => {
  try {
    return BUCKET_BY_HOST[new URL(process.env.NEXT_PUBLIC_BASE_MEDIA_URL).hostname];
  } catch {
    return undefined;
  }
})();

function nextOptimizer(src, width, quality) {
  return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality || 75}`;
}

/**
 * Lấy key s3 của ảnh, hoặc null nếu ảnh không nằm trong bucket của môi trường này.
 *
 * Ảnh nội dung CKEditor dùng src TƯƠNG ĐỐI '/image/<id>' (xem UploadAdapter.tsx), và
 * rewrite '/image/:path*' trỏ tới NEXT_PUBLIC_BASE_MEDIA_URL, nên nó tương đương
 * '<BASE_MEDIA_URL>/image/<id>' và quy được về đúng key. Không quy về thì Next phải tự
 * gọi lại rewrite của chính nó để tải ảnh -> đúng chỗ sinh ra
 * 'Failed to proxy https://statics.flyer.vn/image/... ETIMEDOUT' trong log prod.
 * public/ không có thư mục 'image' nên tiền tố này không đụng ảnh local nào.
 */
function mediaKey(src) {
  if (src.startsWith('/image/')) {
    return src.slice(1);
  }
  if (!src.startsWith('http')) {
    return null;
  }
  let parsed;
  try {
    parsed = new URL(src);
  } catch {
    return null;
  }
  if (BUCKET_BY_HOST[parsed.hostname] !== ENV_BUCKET) {
    return null;
  }
  return parsed.pathname.replace(/^\/+/, '') || null;
}

export default function imgproxyLoader({ src, width, quality }) {
  // Next tự đánh dấu unoptimized cho data:/blob: trước khi gọi loader
  // (get-img-props.js:245) nên ở đây không cần lo 2 dạng đó.
  if (typeof src !== 'string') {
    return src;
  }

  // SVG phải trả về NGUYÊN VẸN, không qua /_next/image cũng không qua imgproxy.
  //
  // Next vốn tự làm việc này, nhưng nhánh đó bị gác bằng isDefaultLoader
  // (get-img-props.js:253):
  //   if (isDefaultLoader && !config.dangerouslyAllowSVG && src...endsWith('.svg'))
  //     unoptimized = true
  // Khai loaderFile là isDefaultLoader thành false -> mất nhánh đó, SVG bị đẩy vào
  // /_next/image và optimizer chặn: 400 '"url" parameter is valid but image type is
  // not allowed'. Đẩy sang imgproxy cũng sai: nó rasterize icon thành webp.
  // Cắt cùng cách Next cắt (bỏ query trước khi xét đuôi).
  if (src.split('?', 1)[0].endsWith('.svg')) {
    return src;
  }
  if (!PREFIX || !ENV_BUCKET) {
    return nextOptimizer(src, width, quality);
  }

  const key = mediaKey(src);
  if (!key) {
    return nextOptimizer(src, width, quality);
  }

  return `${PREFIX}/w:${width}/q:${quality || 75}/${key}.webp`;
}
